import { useCallback, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import ToolTip from "components/design/toolTip";
import { useToast } from "hooks/useToast";
import { DragIcon } from "components/icons/DragIcon";
import Wind from "components/icons/Wind";
import { IncorporationFormSource } from "constants/incorporation";
import {
  ReorderFormTreeBody,
  Stage,
} from "store/apis/incorporationFormBuilder";
import { useReorderFormTreeMutation } from "./useFormTreeNodeMutations";

const STAGE_DRAG_TYPE = "REARRANGE_STAGE";
const STEP_DRAG_TYPE = "REARRANGE_STEP";

type DraftStep = { uuid: string; title: string };
type DraftStage = { uuid: string; title: string; steps: DraftStep[] };

type StageDragItem = { index: number };
type StepDragItem = { index: number; stageIndex: number };

type DraggableStepProps = {
  step: DraftStep;
  index: number;
  stageIndex: number;
  onMoveStep: (
    fromStageIndex: number,
    fromIndex: number,
    toStageIndex: number,
    toIndex: number
  ) => void;
  onStepDragStateChange: (draggingStageIndex: number | null) => void;
};

const DraggableStep = ({
  step,
  index,
  stageIndex,
  onMoveStep,
  onStepDragStateChange,
}: DraggableStepProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: STEP_DRAG_TYPE,
      item: () => {
        onStepDragStateChange(stageIndex);
        return { index, stageIndex };
      },
      end: () => {
        onStepDragStateChange(null);
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index, stageIndex, onStepDragStateChange]
  );

  const [{ isOver }, drop] = useDrop<StepDragItem, void, { isOver: boolean }>(
    () => ({
      accept: STEP_DRAG_TYPE,
      canDrop: (item) => item.stageIndex === stageIndex,
      drop: (draggedItem) => {
        if (draggedItem.stageIndex !== stageIndex) return;
        if (draggedItem.index === index) return;
        onMoveStep(
          draggedItem.stageIndex,
          draggedItem.index,
          stageIndex,
          index
        );
      },
      collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
    }),
    [index, stageIndex, onMoveStep]
  );

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={classNames(
        "t-relative t-flex t-items-center t-gap-2 t-py-2.5",
        {
          "t-opacity-50 !t-cursor-grabbing": isDragging,
          "t-bg-surface-lighter-grey": isOver,
        }
      )}
    >
      {isOver && (
        <div className="t-absolute t-inset-x-0 t-top-0 t-h-0.5 t-rounded-full t-bg-purple" />
      )}
      <span
        className="t-flex t-shrink-0 t-cursor-grab t-items-center t-text-neutral-40 t-select-none"
        title="Drag to reorder"
      >
        <DragIcon />
      </span>
      <ToolTip text={step.title}>
        <span className="t-min-w-0 t-truncate t-text-subtext t-text-text-60">
          {step.title}
        </span>
      </ToolTip>
    </div>
  );
};

type DraggableStageProps = {
  stage: DraftStage;
  index: number;
  onMoveStage: (fromIndex: number, toIndex: number) => void;
  onMoveStep: (
    fromStageIndex: number,
    fromIndex: number,
    toStageIndex: number,
    toIndex: number
  ) => void;
  draggingStepStageIndex: number | null;
  onStepDragStateChange: (draggingStageIndex: number | null) => void;
};

const DraggableStage = ({
  stage,
  index,
  onMoveStage,
  onMoveStep,
  draggingStepStageIndex,
  onStepDragStateChange,
}: DraggableStageProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: STAGE_DRAG_TYPE,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index]
  );

  const [{ isOver }, drop] = useDrop<StageDragItem, void, { isOver: boolean }>(
    () => ({
      accept: STAGE_DRAG_TYPE,
      drop: (draggedItem) => {
        if (draggedItem.index === index) return;
        onMoveStage(draggedItem.index, index);
      },
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [index, onMoveStage]
  );

  drag(drop(ref));

  const isOtherStageDraggingStep =
    draggingStepStageIndex !== null && draggingStepStageIndex !== index;

  return (
    <div
      ref={ref}
      className={classNames("t-relative t-rounded-lg", {
        "t-opacity-50 !t-cursor-grabbing": isDragging,
        "t-bg-surface-lighter-grey": isOver,
      })}
    >
      {isOver && (
        <div className="t-absolute t-inset-x-0 t-top-0 t-h-0.5 t-rounded-full t-bg-purple" />
      )}
      <Accordion.Item value={stage.uuid}>
        <Accordion.Trigger disabled={isOtherStageDraggingStep}>
          <span
            className={classNames(
              "t-flex t-shrink-0 t-cursor-grab t-items-center t-text-neutral-40 t-select-none",
              { "t-opacity-20": isOtherStageDraggingStep }
            )}
            title="Drag to reorder"
          >
            <DragIcon />
          </span>
          <ToolTip text={stage.title}>
            <span
              className={classNames(
                "t-min-w-0 t-flex-1 t-truncate t-text-subtext t-text-text-100",
                {
                  "t-opacity-20": isOtherStageDraggingStep,
                }
              )}
            >
              {stage.title}
            </span>
          </ToolTip>
        </Accordion.Trigger>
        <Accordion.Content
          className={classNames({
            "t-pointer-events-none t-opacity-40": isOtherStageDraggingStep,
          })}
        >
          <div className="t-flex t-flex-col t-pl-6">
            {stage.steps.map((step, stepIndex) => (
              <DraggableStep
                key={step.uuid}
                step={step}
                index={stepIndex}
                stageIndex={index}
                onMoveStep={onMoveStep}
                onStepDragStateChange={onStepDragStateChange}
              />
            ))}
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </div>
  );
};

const RearrangeStepsEmptyState = () => (
  <div className="t-flex t-flex-col t-items-center t-gap-3 t-py-10 t-text-neutral-30">
    <Wind />
    <p className="t-m-0 t-text-subtext t-text-text-30">
      There is no stage and step to rearrange
    </p>
  </div>
);

const toDraftStages = (stages: Stage[]): DraftStage[] =>
  stages.map((stage) => ({
    uuid: stage.uuid,
    title: stage.title,
    steps: stage.steps.map((step) => ({ uuid: step.uuid, title: step.title })),
  }));

const toReorderBody = (stages: DraftStage[]): ReorderFormTreeBody => ({
  stages: stages.map((stage) => ({
    uuid: stage.uuid,
    steps: stage.steps.map((step) => step.uuid),
  })),
});

type RearrangeStepsModalContentProps = {
  close: () => void;
  treeId: string;
  source: IncorporationFormSource;
  stages: Stage[];
};

const RearrangeStepsModalContent = ({
  close,
  treeId,
  source,
  stages,
}: RearrangeStepsModalContentProps) => {
  const { alertToast, successToast } = useToast();
  const [draftStages, setDraftStages] = useState<DraftStage[]>(() =>
    toDraftStages(stages)
  );
  const [expandedStageIds, setExpandedStageIds] = useState<Set<string>>(
    new Set()
  );

  const [reorderFormTree, { isLoading }] = useReorderFormTreeMutation({
    source,
  });

  const [draggingStepStageIndex, setDraggingStepStageIndex] = useState<
    number | null
  >(null);

  const handleExpandedStageIdsChange = useCallback((value: string[]) => {
    setExpandedStageIds(new Set(value));
  }, []);

  const handleMoveStage = useCallback((fromIndex: number, toIndex: number) => {
    setDraftStages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const handleMoveStep = useCallback(
    (
      fromStageIndex: number,
      fromIndex: number,
      toStageIndex: number,
      toIndex: number
    ) => {
      setDraftStages((prev) => {
        const next = prev.map((stage) => ({
          ...stage,
          steps: [...stage.steps],
        }));
        const [moved] = next[fromStageIndex].steps.splice(fromIndex, 1);
        next[toStageIndex].steps.splice(toIndex, 0, moved);
        return next;
      });
    },
    []
  );

  const handleSave = async () => {
    try {
      await reorderFormTree({
        treeId,
        body: toReorderBody(draftStages),
      }).unwrap();
      successToast({ message: "Template changes saved" });
      close();
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Content size="regular">
      <Modal.Header>
        <Modal.Title>Rearrange Stages & Steps</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        {draftStages.length === 0 ? (
          <RearrangeStepsEmptyState />
        ) : (
          <Accordion.Root
            type="multiple"
            value={Array.from(expandedStageIds)}
            onValueChange={handleExpandedStageIdsChange}
            className="t-flex t-flex-col t-gap-4"
          >
            {draftStages.map((stage, index) => (
              <DraggableStage
                key={stage.uuid}
                stage={stage}
                index={index}
                onMoveStage={handleMoveStage}
                onMoveStep={handleMoveStep}
                draggingStepStageIndex={draggingStepStageIndex}
                onStepDragStateChange={setDraggingStepStageIndex}
              />
            ))}
          </Accordion.Root>
        )}
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button type="button" disabled={isLoading}>
            Discard
          </Button>
        </Modal.RawClose>
        <Button
          type="button"
          customType="primary"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </Modal.FooterButtonGroup>
    </Modal.Content>
  );
};

type RearrangeStepsModalProps = {
  isOpen: boolean;
  close: () => void;
  treeId: string;
  source: IncorporationFormSource;
  stages: Stage[];
};

export const RearrangeStepsModal = ({
  isOpen,
  close,
  treeId,
  source,
  stages,
}: RearrangeStepsModalProps) => (
  <Modal.Root open={isOpen} onOpenChange={(nextOpen) => !nextOpen && close()}>
    {isOpen && (
      <RearrangeStepsModalContent
        key={treeId}
        close={close}
        treeId={treeId}
        source={source}
        stages={stages}
      />
    )}
  </Modal.Root>
);
